/**
 * PGD Registry
 * Central location to register all PGDs
 * When you add a new PGD version, export it here and add to the map
 */

import type { PGD } from "../types";
import { pgdNitrofurantoinUti } from "./pgd-1a-nitrofurantoin-uti.v1.2";
import { pgdAciclovirShingles } from "./pgd-2a-aciclovir-shingles.v1.0";
import { pgdValaciclovirShingles } from "./pgd-2b-valaciclovir-shingles.v1.0";
import { pgdFlucloxacillinInsectBites } from "./pgd-4a-flucloxacillin-insectbites.v1.0";
import { pgdClarithromycinInsectBites } from "./pgd-4b-clarithromycin-insectbites.v1.0";
import { pgdErythromycinInsectBites } from "./pgd-4c-erythromycin-insectbites.v1.0";
import { protocolHydrogenPeroxideImpetigo } from "./protocol-3a-h2o2-impetigo.v1.0";
import { pgdFusidicImpetigo } from "./pgd-3b-fusidic-impetigo.v1.0";
import { pgdFlucloxacillinImpetigo } from "./pgd-3c-fluclox-impetigo.v1.0";
import { pgdClarithromycinImpetigo } from "./pgd-3d-clari-impetigo.v1.0";
import { pgdErythromycinImpetigo } from "./pgd-3e-eryth-impetigo.v1.0";
import { pgdPenvSoreThroat } from "./pgd-5a-penv-sorethroat.v1.0";
import { pgdClarithromycinSoreThroat } from "./pgd-5b-clari-sorethroat.v1.0";
import { pgdErythromycinSoreThroat } from "./pgd-5c-eryth-sorethroat.v1.0";
import { pgdPhenazoneLidocaineAom } from "./pgd-7a-phenazlido-aom.v1.0";
import { pgdAmoxicillinAom } from "./pgd-7b-amox-aom.v1.0";
import { pgdClarithromycinAom } from "./pgd-7c-clari-aom.v1.0";
import { pgdErythromycinAom } from "./pgd-7d-eryth-aom.v1.0";
import { pgdFluticasoneSinusitis } from "./pgd-6a-fluticasone-sinusitis.v1.0";
import { pgdMometasoneSinusitis } from "./pgd-6b-mometasone-sinusitis.v1.0";
import { pgdPenvSinusitis } from "./pgd-6c-penv-sinusitis.v1.0";
import { pgdClarithromycinSinusitis } from "./pgd-6d-clari-sinusitis.v1.0";
import { pgdDoxycyclineSinusitis } from "./pgd-6e-doxy-sinusitis.v1.0";
import { pgdErythromycinSinusitis } from "./pgd-6f-eryth-sinusitis.v1.0";
import { pgdAhtWtTirzepatide } from "./aht/pgd-aht-wt-tirzepatide";
import { pgdAhtWtSemaglutide } from "./aht/pgd-aht-wt-semaglutide";
import { pgdAhtWtOralSemaglutide } from "./aht/pgd-aht-wt-oral-semaglutide";
import { pgdAhtWtFoundayo } from "./aht/pgd-aht-wt-foundayo";
import { pgdAhtWtLiraglutide } from "./aht/pgd-aht-wt-liraglutide";
import { pgdAhtWtMysimba } from "./aht/pgd-aht-wt-mysimba";
import { pgdAhtWtOrlistat } from "./aht/pgd-aht-wt-orlistat";

/**
 * Map of pgdId → PGD config
 * The engine resolves by pgdId and loads the matching config
 */
export const pgdRegistry: Record<string, PGD> = {
  "pgd-1a-nitrofurantoin-uti": pgdNitrofurantoinUti,
  "pgd-2a-aciclovir-shingles": pgdAciclovirShingles,
  "pgd-2b-valaciclovir-shingles": pgdValaciclovirShingles,
  "pgd-4a-flucloxacillin-insectbites": pgdFlucloxacillinInsectBites,
  "pgd-4b-clarithromycin-insectbites": pgdClarithromycinInsectBites,
  "pgd-4c-erythromycin-insectbites": pgdErythromycinInsectBites,
  "protocol-3a-h2o2-impetigo": protocolHydrogenPeroxideImpetigo,
  "pgd-3b-fusidic-impetigo": pgdFusidicImpetigo,
  "pgd-3c-fluclox-impetigo": pgdFlucloxacillinImpetigo,
  "pgd-3d-clari-impetigo": pgdClarithromycinImpetigo,
  "pgd-3e-eryth-impetigo": pgdErythromycinImpetigo,
  "pgd-5a-penv-sorethroat": pgdPenvSoreThroat,
  "pgd-5b-clarithromycin-sorethroat": pgdClarithromycinSoreThroat,
  "pgd-5c-erythromycin-sorethroat": pgdErythromycinSoreThroat,
  "pgd-7a-phenazlido-aom": pgdPhenazoneLidocaineAom,
  "pgd-7b-amox-aom": pgdAmoxicillinAom,
  "pgd-7c-clari-aom": pgdClarithromycinAom,
  "pgd-7d-eryth-aom": pgdErythromycinAom,
  "pgd-6a-fluticasone-sinusitis": pgdFluticasoneSinusitis,
  "pgd-6b-mometasone-sinusitis": pgdMometasoneSinusitis,
  "pgd-6c-penv-sinusitis": pgdPenvSinusitis,
  "pgd-6d-clari-sinusitis": pgdClarithromycinSinusitis,
  "pgd-6e-doxy-sinusitis": pgdDoxycyclineSinusitis,
  "pgd-6f-eryth-sinusitis": pgdErythromycinSinusitis,
  "pgd-aht-wt-tirzepatide": pgdAhtWtTirzepatide,
  "pgd-aht-wt-semaglutide": pgdAhtWtSemaglutide,
  "pgd-aht-wt-oral-semaglutide": pgdAhtWtOralSemaglutide,
  "pgd-aht-wt-foundayo": pgdAhtWtFoundayo,
  "pgd-aht-wt-liraglutide": pgdAhtWtLiraglutide,
  "pgd-aht-wt-mysimba": pgdAhtWtMysimba,
  "pgd-aht-wt-orlistat": pgdAhtWtOrlistat,
};

/**
 * Helper to look up a PGD by ID
 * Throws if not found (should never happen in production)
 */
export function getPgd(pgdId: string): PGD {
  const pgd = pgdRegistry[pgdId];
  if (!pgd) {
    throw new Error(
      `PGD not found: ${pgdId}. Register it in pgdRegistry before use.`
    );
  }
  return pgd;
}

/**
 * Helper to check if a PGD is currently valid
 */
export function isPgdValid(pgdId: string, atDate: string = new Date().toISOString()): boolean {
  const pgd = pgdRegistry[pgdId];
  if (!pgd) return false;
  return atDate >= pgd.validFrom && atDate <= pgd.expiryDate;
}
